import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listDesigns, deleteDesign } from "../lib/api";
import HeaderBar from "../components/HeaderBar";
import ProjectCard from "../components/ProjectCard";
import NewProjectDialog from "../components/NewProjectDialog";
import DeleteProjectDialog from "../components/DeleteProjectDialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // V2.0.84 — If the user arrived from the Landing marketplace with a
  // `first_prompt` query param, stash it so the very next design we
  // open auto-sends it as the first chat. Fires once; the param is
  // cleared to keep the URL clean.
  useEffect(() => {
    const fp = searchParams.get("first_prompt");
    if (fp) {
      try {
        sessionStorage.setItem("blueprintai.first_prompt", fp);
      } catch { /* private-browsing quotas */ }
      const next = new URLSearchParams(searchParams);
      next.delete("first_prompt");
      setSearchParams(next, { replace: true });
      // Open the new-project dialog automatically so the user is one
      // click away from the design that will run their prompt.
      setDialogOpen(true);
      toast(`Prompt loaded — create a project to run it: "${fp.slice(0, 60)}${fp.length > 60 ? "…" : ""}"`);
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listDesigns();
      setDesigns(r.data || []);
    } catch (e) {
      toast.error("Could not load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDialog = () => setDialogOpen(true);

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.design_id;
    const name = pendingDelete.name;
    try {
      await deleteDesign(id);
      setDesigns((prev) => prev.filter((d) => d.design_id !== id));
      toast.success(`Deleted "${name}"`);
      setPendingDelete(null);
    } catch (e) {
      toast.error("Could not delete project");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <HeaderBar />
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 pb-8 border-b border-zinc-800">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brass-400 mb-2">
              // My Projects
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-zinc-50 tracking-tight">
              Studio
            </h1>
            <p className="font-body text-sm text-zinc-400 mt-2 max-w-md">
              Every home you&apos;ve designed with the architectural assistant. Pick up where you left off.
            </p>
          </div>
          <button
            onClick={openDialog}
            data-testid="create-design-btn"
            className="inline-flex items-center gap-2 bg-brass-600 hover:bg-brass-500 text-zinc-950 font-mono text-xs uppercase tracking-widest px-5 py-3 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New design
          </button>
        </div>

        <div className="mt-8">
          {loading ? (
            <div
              className="font-mono text-[10px] uppercase tracking-widest text-brass-400 architecting-dot py-20 text-center"
              data-testid="dashboard-loading"
            >
              Loading projects...
            </div>
          ) : designs.length === 0 ? (
            <div
              className="border border-dashed border-zinc-800 p-10 lg:p-16 text-center"
              data-testid="dashboard-empty"
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-3">
                // No projects yet
              </div>
              <h2 className="font-display text-2xl text-zinc-50 tracking-tight">
                Start your first home.
              </h2>
              <p className="font-body text-sm text-zinc-400 mt-2 max-w-md mx-auto">
                Create a project, then describe what you want. The architect will model it in 3D as you type.
              </p>
              <button
                onClick={openDialog}
                className="mt-6 inline-flex items-center gap-2 bg-brass-600 hover:bg-brass-500 text-zinc-950 font-mono text-xs uppercase tracking-widest px-5 py-3 transition-colors"
                data-testid="empty-create-btn"
              >
                <Plus className="w-4 h-4" />
                New design
              </button>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
              data-testid="dashboard-grid"
            >
              {designs.map((d) => (
                <ProjectCard
                  key={d.design_id}
                  design={d}
                  onRequestDelete={(target) => setPendingDelete(target)}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      <NewProjectDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <DeleteProjectDialog
        design={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
